// @ts-nocheck
/**
 * DungeonTab - ダンジョン情報タブ
 *
 * TwinKeyダンジョンでの鍵利用情報、ドロップ情報を表示
 */

import React, { useMemo } from 'react';
import { COLORS } from '../../../styles/cardDexStyles';
import {
  getRelatedContinents,
  calculateAttributeSynergy,
} from '../../../../../twinKey/core/dungeon/continentSelector';
import {
  getCardDropTable,
  getAllCategoryDropTables,
} from '../../../../../twinKey/content/drops/dropTables';
import { ATTRIBUTE_SCORES } from '../../../../../twinKey/data/constants';
import { ATTRIBUTE_COLORS } from '../../../../../utils/constants';

// ========================================
// 大陸情報
// ========================================

const CONTINENT_INFO = {
  time_rift: {
    name: '時空の狭間',
    icon: '🌀',
    description: '時間が歪んだ不思議な空間',
    attribute: '未来',
  },
  primal_depths: {
    name: '原始の深淵',
    icon: '🌿',
    description: '太古の生命が息づく密林',
    attribute: '原始',
  },
  aviculus: {
    name: '巨鳥の島',
    icon: '🔥',
    description: '炎に包まれた熱帯の島',
    attribute: '炎',
  },
  black_curse: {
    name: '黒呪の大陸',
    icon: '🌙',
    description: '闇に覆われた呪われし大地',
    attribute: '闇',
  },
  steam_boundary: {
    name: '蒸気の境界',
    icon: '💨',
    description: '炎と水が交わる境界線',
    attribute: '炎/水',
  },
  twilight_realm: {
    name: '黄昏の境界',
    icon: '🌅',
    description: '光と闇が均衡する神秘の地',
    attribute: '光/闇',
  },
  unknown: {
    name: '未知の領域',
    icon: '❓',
    description: '未踏の異世界',
    attribute: 'なし',
  },
};

// ========================================
// 鍵情報セクション
// ========================================

const KeyInfoSection = ({ card }) => {
  const attributeScore = ATTRIBUTE_SCORES[card.attribute] || ATTRIBUTE_SCORES['なし'];
  const relatedContinents = useMemo(() => getRelatedContinents(card.attribute), [card.attribute]);
  const attributeColor = ATTRIBUTE_COLORS[card.attribute]?.primary || COLORS.textPrimary;

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 鍵としての特性</div>
      <div style={sectionStyles.content}>
        {/* 属性スコア */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '8px' }}>
            属性スコア（
            <span style={{ color: attributeColor }}>{card.attribute}</span>
            ）
          </div>
          <div style={scoreStyles.container}>
            <div style={scoreStyles.item}>
              <span style={scoreStyles.label}>🔥 熱量</span>
              <span style={{
                ...scoreStyles.value,
                color: attributeScore.heat > 0 ? '#ff6b6b' : attributeScore.heat < 0 ? '#6b9eff' : COLORS.textSecondary,
              }}>
                {attributeScore.heat > 0 ? '+' : ''}{attributeScore.heat}
              </span>
            </div>
            <div style={scoreStyles.item}>
              <span style={scoreStyles.label}>🌙 闇力</span>
              <span style={{
                ...scoreStyles.value,
                color: attributeScore.darkness > 0 ? '#9b59b6' : attributeScore.darkness < 0 ? '#f1c40f' : COLORS.textSecondary,
              }}>
                {attributeScore.darkness > 0 ? '+' : ''}{attributeScore.darkness}
              </span>
            </div>
            <div style={scoreStyles.item}>
              <span style={scoreStyles.label}>⚡ 時力</span>
              <span style={{
                ...scoreStyles.value,
                color: attributeScore.future > 0 ? '#1abc9c' : attributeScore.future < 0 ? '#e67e22' : COLORS.textSecondary,
              }}>
                {attributeScore.future > 0 ? '+' : ''}{attributeScore.future}
              </span>
            </div>
          </div>
        </div>

        {/* 関連大陸 */}
        <div>
          <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '8px' }}>
            このカードを鍵に含めると行きやすい大陸:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {relatedContinents.map((continentId) => {
              const continent = CONTINENT_INFO[continentId];
              if (!continent) return null;
              return (
                <div key={continentId} style={continentStyles.row}>
                  <span style={continentStyles.icon}>{continent.icon}</span>
                  <div style={continentStyles.info}>
                    <span style={continentStyles.name}>{continent.name}</span>
                    <span style={continentStyles.desc}>{continent.description}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ヒント */}
        <div style={{ marginTop: '12px', fontSize: '11px', color: COLORS.textSecondary }}>
          ※ 2枚の鍵の属性組み合わせで最終的な大陸が決定されます
        </div>
      </div>
    </div>
  );
};

// ========================================
// ドロップ情報セクション
// ========================================

const DropInfoSection = ({ card }) => {
  // カードID固有ドロップ
  const cardDropTable = useMemo(() => getCardDropTable(card.id), [card.id]);

  // カテゴリベースのドロップ（複数カテゴリ対応）
  // card.categoryText は【】付きの元テキスト形式（例: 【ドラゴン】【ビースト】）
  const categoryDropTables = useMemo(() => getAllCategoryDropTables(card.categoryText || card.category), [card.categoryText, card.category]);

  const hasDropInfo = cardDropTable || categoryDropTables.length > 0;

  // 属性結晶の名前
  const attributeCrystalMap = {
    '炎': '炎の結晶',
    '水': '水の結晶',
    '光': '光の結晶',
    '闇': '闇の結晶',
    '原始': '原始の結晶',
    '未来': '未来の結晶',
    'なし': '魔力の核',
  };
  const attributeCrystal = attributeCrystalMap[card.attribute] || '魔力の核';

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ ドロップ情報</div>
      <div style={sectionStyles.content}>
        {/* 固有ドロップ */}
        {cardDropTable && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: COLORS.textGold, marginBottom: '8px' }}>
              🎁 固有ドロップ（このカードのみ）
            </div>

            {/* 確定ドロップ */}
            {cardDropTable.guaranteed && cardDropTable.guaranteed.length > 0 && (
              <div style={dropStyles.section}>
                <span style={dropStyles.label}>確定:</span>
                <div style={dropStyles.items}>
                  {cardDropTable.guaranteed.map((drop, i) => (
                    <span key={i} style={dropStyles.item}>
                      {drop.item} x{drop.count}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 通常ドロップ */}
            {cardDropTable.materials && cardDropTable.materials.length > 0 && (
              <div style={dropStyles.section}>
                <span style={dropStyles.label}>通常:</span>
                <div style={dropStyles.items}>
                  {cardDropTable.materials.map((mat, i) => (
                    <span key={i} style={dropStyles.item}>
                      {mat.item} ({(mat.chance * 100).toFixed(0)}%)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* レアドロップ */}
            {cardDropTable.rarePool && cardDropTable.rarePool.length > 0 && (
              <div style={dropStyles.section}>
                <span style={{ ...dropStyles.label, color: COLORS.textGold }}>レア:</span>
                <div style={dropStyles.items}>
                  {cardDropTable.rarePool.map((rare, i) => (
                    <span key={i} style={{ ...dropStyles.item, color: COLORS.textGold }}>
                      ⭐ {rare.item}
                    </span>
                  ))}
                  <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                    ({(cardDropTable.rareChance * 100).toFixed(0)}%確率)
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* カテゴリドロップ（複数カテゴリ対応） */}
        {categoryDropTables.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: COLORS.accent, marginBottom: '8px' }}>
              📦 カテゴリドロップ（{categoryDropTables.length}種類）
            </div>

            {categoryDropTables.map(({ category: catName, table: categoryDropTable }, catIndex) => (
              <div key={catIndex} style={{ marginBottom: catIndex < categoryDropTables.length - 1 ? '12px' : 0 }}>
                {/* カテゴリ名 */}
                <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '4px' }}>
                  {catName}
                </div>

                {/* 通常ドロップ */}
                {categoryDropTable.materials && categoryDropTable.materials.length > 0 && (
                  <div style={dropStyles.section}>
                    <span style={dropStyles.label}>通常:</span>
                    <div style={dropStyles.items}>
                      {categoryDropTable.materials.map((mat, i) => (
                        <span key={i} style={dropStyles.item}>
                          {mat.item} ({(mat.chance * 100).toFixed(0)}%)
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* レアドロップ */}
                {categoryDropTable.rarePool && categoryDropTable.rarePool.length > 0 && (
                  <div style={dropStyles.section}>
                    <span style={{ ...dropStyles.label, color: COLORS.textGold }}>レア:</span>
                    <div style={dropStyles.items}>
                      {categoryDropTable.rarePool.map((rare, i) => (
                        <span key={i} style={{ ...dropStyles.item, color: COLORS.textGold }}>
                          ⭐ {rare.item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 基本ドロップ（固有/カテゴリがない場合） */}
        {!hasDropInfo && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '8px' }}>
              📦 基本ドロップ
            </div>
            <div style={dropStyles.section}>
              <span style={dropStyles.label}>通常:</span>
              <div style={dropStyles.items}>
                <span style={dropStyles.item}>{attributeCrystal}</span>
                <span style={dropStyles.item}>魔力の核</span>
              </div>
            </div>
          </div>
        )}

        {/* 属性素材情報 */}
        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>
          <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
            💎 属性素材: <span style={{ color: ATTRIBUTE_COLORS[card.attribute]?.primary || COLORS.textPrimary }}>
              {attributeCrystal}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================================
// 相性情報セクション
// ========================================

const SynergyHintSection = ({ card }) => {
  // 相性の良い属性を計算
  const synergies = useMemo(() => {
    const attributes = ['炎', '水', '光', '闇', '原始', '未来'];
    return attributes
      .filter((attr) => attr !== card.attribute)
      .map((attr) => ({
        attribute: attr,
        ...calculateAttributeSynergy(card.attribute, attr),
      }))
      .filter((s) => s.synergy !== 0)
      .sort((a, b) => b.synergy - a.synergy);
  }, [card.attribute]);

  if (synergies.length === 0) {
    return null;
  }

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 鍵ペア相性</div>
      <div style={sectionStyles.content}>
        <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginBottom: '8px' }}>
          他の属性カードと組み合わせた時の効果:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {synergies.slice(0, 4).map((s) => (
            <div key={s.attribute} style={synergyStyles.row}>
              <span style={{
                ...synergyStyles.attribute,
                color: ATTRIBUTE_COLORS[s.attribute]?.primary || COLORS.textPrimary,
              }}>
                {s.attribute}
              </span>
              <span style={{
                ...synergyStyles.synergy,
                color: s.synergy > 0 ? COLORS.textSuccess : COLORS.textDanger,
              }}>
                {s.synergy > 0 ? '+' : ''}{s.synergy}
              </span>
              <span style={synergyStyles.desc}>{s.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

const DungeonTab = ({ card }) => {
  return (
    <div>
      {/* 鍵情報 */}
      <KeyInfoSection card={card} />

      {/* ドロップ情報 */}
      <DropInfoSection card={card} />

      {/* 相性情報 */}
      <SynergyHintSection card={card} />

      {/* 注釈 */}
      <div style={{ marginTop: '16px', fontSize: '11px', color: COLORS.textSecondary }}>
        ※ ツインキーダンジョンはメニュー画面の「異界の扉」から挑戦できます
      </div>
    </div>
  );
};

// ========================================
// スタイル定義
// ========================================

const sectionStyles = {
  container: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '14px',
    color: COLORS.textGold,
    marginBottom: '12px',
  },
  content: {
    padding: '12px',
    background: 'rgba(30, 30, 50, 0.6)',
    borderRadius: '8px',
  },
};

const scoreStyles = {
  container: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
  },
  label: {
    fontSize: '12px',
    color: COLORS.textSecondary,
  },
  value: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
};

const continentStyles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '6px',
  },
  icon: {
    fontSize: '24px',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
  },
  name: {
    fontSize: '13px',
    color: COLORS.textPrimary,
    fontWeight: 'bold',
  },
  desc: {
    fontSize: '11px',
    color: COLORS.textSecondary,
  },
};

const dropStyles = {
  section: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '6px',
  },
  label: {
    fontSize: '11px',
    color: COLORS.textSecondary,
    width: '40px',
    flexShrink: 0,
  },
  items: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  item: {
    fontSize: '12px',
    color: COLORS.textPrimary,
    padding: '2px 6px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '4px',
  },
};

const synergyStyles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px',
  },
  attribute: {
    fontSize: '12px',
    fontWeight: 'bold',
    width: '40px',
  },
  synergy: {
    fontSize: '12px',
    fontWeight: 'bold',
    width: '30px',
  },
  desc: {
    fontSize: '11px',
    color: COLORS.textSecondary,
    flex: 1,
  },
};

export default DungeonTab;
